/**
 * The plan's small SVG handle glyphs: the wall hit strip, trash, add-bump,
 * the chamfer corner handle (drag triangle and cut diagonal), the rotation
 * handle, the selected edge, and the draggable edge. Presentational: points
 * arrive projected to screen space (a chamfer corner with its projection),
 * and the placement math (cornerHandlePlacement, cornerHandleTriangle,
 * chamferEndpoints) lives in the editor core.
 *
 * Exception to "presentational": the mutation-BUTTON glyphs (TrashIcon,
 * AddBumpIcon) hide themselves in read-only mode via the editor-state
 * context, so every render site gets the gate for free. Selection-target
 * glyphs (ChamferCornerHandle, DragEdge) always render: selection stays live
 * read-only.
 */

import { type LineFrame, type Point, WALL_THICKNESS } from '@kaiizen/kai-floor-plan';
import {
  type ChamferData,
  chamferEndpoints,
  cornerHandlePlacement,
  cornerHandleTriangle,
  hasChamfer,
  perpCursor,
  svgPoints,
  useFloorPlanEditorState,
} from '@kaiizen/kai-floor-plan-editor-core';
import { handleStroke, openColor, selection, white } from '../../styles';
import { ALCOVE_EDGE_HIT_STYLE, EDGE_HIT_STYLE, EDGE_SEL_STYLE, HANDLE_CORNER_STYLE, HANDLE_STYLE, WALL_HIT_STYLE } from './planCss';

/** A wall's hit strip reaches this far (px) into the room past the inner line… */
export const WALL_HIT_INSET_PX = 7;
/** …and this far (px) beyond the outer line, past the band. */
export const WALL_HIT_OUTSET_PX = 4;

/**
 * The invisible hit target of a wall run in plan: from just inside the inner
 * line out across the wall's thickness and a little beyond, so a click
 * anywhere on the drawn wall — its band included — lands on the wall. `a`
 * and `b` are the inner line's screen endpoints; `perp` its outward unit
 * direction (plan and screen share orientation).
 */
export function WallHitStrip({ a, b, perp, scale, handle, cursor }: {
  a: Point;
  b: Point;
  perp: Point;
  scale: number;
  handle: string;
  cursor: string;
}): React.JSX.Element {
  const inset = WALL_HIT_INSET_PX, outset = WALL_THICKNESS * scale + WALL_HIT_OUTSET_PX;
  const corners = [
    { x: a.x - perp.x * inset, y: a.y - perp.y * inset },
    { x: b.x - perp.x * inset, y: b.y - perp.y * inset },
    { x: b.x + perp.x * outset, y: b.y + perp.y * outset },
    { x: a.x + perp.x * outset, y: a.y + perp.y * outset },
  ];
  return <polygon css={WALL_HIT_STYLE} data-handle={handle} style={{ cursor }} points={svgPoints(corners)} />;
}

export function TrashIcon({ p, handle }: { p: Point; handle: string }): React.JSX.Element | null {
  const { st } = useFloorPlanEditorState();
  const { x, y } = p;
  if (st.readOnly) return null;
  return (
    <g data-handle={handle} style={{ cursor: 'pointer' }}>
      <circle cx={x} cy={y} r={13} fill={white} stroke={openColor} strokeWidth={2} />
      <path d={`M ${x - 6} ${y - 4} H ${x + 6}`} stroke={openColor} strokeWidth={1.7} strokeLinecap='round' />
      <path d={`M ${x - 2.5} ${y - 4} V ${y - 6} H ${x + 2.5} V ${y - 4}`} fill='none' stroke={openColor} strokeWidth={1.5} />
      <path
        d={`M ${x - 4.5} ${y - 3} L ${x - 3.5} ${y + 6} H ${x + 3.5} L ${x + 4.5} ${y - 3}`}
        fill='none'
        stroke={openColor}
        strokeWidth={1.5}
        strokeLinejoin='round'
      />
      <path d={`M ${x - 1.5} ${y - 1} V ${y + 4} M ${x + 1.5} ${y - 1} V ${y + 4}`} stroke={openColor} strokeWidth={1.3} strokeLinecap='round' />
    </g>
  );
}

export function AddBumpIcon({ p }: { p: Point }): React.JSX.Element | null {
  const { st } = useFloorPlanEditorState();
  const { x, y } = p;
  if (st.readOnly) return null;
  return (
    <g data-handle='addbump' style={{ cursor: 'pointer' }}>
      <circle cx={x} cy={y} r={11} fill={white} stroke={handleStroke} strokeWidth={2} />
      <line x1={x - 5} y1={y} x2={x + 5} y2={y} stroke={handleStroke} strokeWidth={2} />
      <line x1={x} y1={y - 5} x2={x} y2={y + 5} stroke={handleStroke} strokeWidth={2} />
    </g>
  );
}

/** A corner's drag triangle (editor-core's `cornerHandleTriangle`), spun about its anchor by an SVG transform. */
function TriHandle({ p, sx, sy, handle, selected, cursor, angle = 0 }: {
  p: Point;
  sx: number;
  sy: number;
  handle: string;
  selected: boolean;
  cursor: string;
  /** Host rotation in degrees: spins the glyph about its anchor so it hugs a rotated corner. */
  angle?: number;
}): React.JSX.Element {
  return (
    <polygon
      css={[HANDLE_STYLE, HANDLE_CORNER_STYLE]}
      data-handle={handle}
      style={{ cursor, fill: selected ? selection : undefined }}
      points={svgPoints(cornerHandleTriangle(p, sx, sy))}
      transform={angle ? `rotate(${angle} ${p.x} ${p.y})` : undefined}
    />
  );
}

/** How far (px) a rotation handle stands out past its host's right side. */
const ROTATE_HANDLE_REACH_PX = 20;

/** Where a rotation handle sits: `ROTATE_HANDLE_REACH_PX` out from the right side's midpoint, along `outward`. */
const rotateHandlePoint = (rightSideMidpoint: Point, outward: Point): Point => ({
  x: rightSideMidpoint.x + outward.x * ROTATE_HANDLE_REACH_PX,
  y: rightSideMidpoint.y + outward.y * ROTATE_HANDLE_REACH_PX,
});

/**
 * Rotation handle: a circular-arrow glyph on a stick from the host's
 * `center`, standing out past its local 0° side. `rightSideMidpoint` is that
 * side's screen midpoint and `outward` its outward unit direction.
 */
export function RotateHandle({ center, rightSideMidpoint, outward, handle }: {
  center: Point;
  rightSideMidpoint: Point;
  outward: Point;
  handle: string;
}): React.JSX.Element {
  const { x, y } = rotateHandlePoint(rightSideMidpoint, outward);
  return (
    <>
      <line x1={center.x} y1={center.y} x2={x} y2={y} stroke={handleStroke} strokeWidth={1} pointerEvents='none' />
      <g data-handle={handle} style={{ cursor: 'grab' }}>
        <circle cx={x} cy={y} r={11} fill={white} stroke={handleStroke} strokeWidth={2} />
        <path d={`M ${x + 5} ${y} A 5 5 0 1 1 ${x} ${y - 5}`} fill='none' stroke={handleStroke} strokeWidth={1.8} strokeLinecap='round' />
        <polygon points={`${x + 3.5},${y - 5} ${x - 0.5},${y - 8} ${x - 0.5},${y - 2}`} fill={handleStroke} />
      </g>
    </>
  );
}

/**
 * THE selected-edge visual: every selected edge renders through this one
 * component, as a single selection-color line with no outline. Never style a
 * selected edge any other way.
 */
export function EdgeSel({ points }: { points: Point[] }): React.JSX.Element {
  return (
    <g pointerEvents='none'>
      <polyline css={EDGE_SEL_STYLE} points={svgPoints(points)} />
    </g>
  );
}

/** Chamfer diagonal line between two pre-computed screen points. */
function ChamferDiagonal({ a, b, handle, cursor, selected }: {
  a: Point;
  b: Point;
  handle: string;
  cursor: string;
  selected: boolean;
}): React.JSX.Element {
  return (
    <>
      {selected && <EdgeSel points={[a, b]} />}
      <line css={EDGE_HIT_STYLE} data-handle={handle} style={{ cursor }} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
    </>
  );
}

/**
 * A chamfer corner's controls: its drag triangle and, once the corner is cut,
 * the cut's diagonal hit line. `chamfer` is in model space and `project` maps
 * it to the screen. `handle` is the corner's `data-handle` payload, after
 * `tri:` and `diag:`. The triangle takes `cursor` and spins by `angle`. The
 * diagonal drags across `diagonalFrame` (the cut's line, for a host that has
 * one), otherwise it takes `cursor` too, and it is highlighted while
 * `diagonalSelected`. `showTriangle` and `showDiagonal` gate the two parts.
 */
export function ChamferCornerHandle({
  chamfer,
  project,
  handle,
  selected,
  cursor,
  angle,
  diagonalFrame = null,
  diagonalSelected = selected,
  showTriangle = true,
  showDiagonal = true,
}: {
  chamfer: ChamferData;
  project: (point: Point) => Point;
  handle: string;
  selected: boolean;
  cursor: string;
  angle?: number;
  diagonalFrame?: LineFrame | null;
  diagonalSelected?: boolean;
  showTriangle?: boolean;
  showDiagonal?: boolean;
}): React.JSX.Element {
  const placement = cornerHandlePlacement(chamfer);
  const [diagonalStart, diagonalEnd] = chamferEndpoints(chamfer, project);
  const diagonalCursor = diagonalFrame ? perpCursor({ x: -diagonalFrame.perp.x, y: -diagonalFrame.perp.y }) : cursor;
  return (
    <>
      {showTriangle && (
        <TriHandle p={project(placement.pt)} sx={placement.sx} sy={placement.sy} handle={`tri:${handle}`} selected={selected} cursor={cursor} angle={angle} />
      )}
      {showDiagonal && hasChamfer(chamfer.ch) && (
        <ChamferDiagonal a={diagonalStart} b={diagonalEnd} handle={`diag:${handle}`} cursor={diagonalCursor} selected={diagonalSelected} />
      )}
    </>
  );
}

/** Draggable edge: visible selected-edge line + fat invisible hit line, given pre-projected screen points. */
export function DragEdge({ a, b, handle, cursor }: {
  a: Point;
  b: Point;
  handle: string;
  cursor: string;
}): React.JSX.Element {
  return (
    <>
      <EdgeSel points={[a, b]} />
      <line css={ALCOVE_EDGE_HIT_STYLE} data-handle={handle} style={{ cursor }} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />
    </>
  );
}
